import { useState, useContext, useEffect } from "react";
import {
  Flex,
  Box,
  Text,
  SimpleGrid,
  Button,
  useDisclosure,
  useToast,
  useColorModeValue,
} from "@chakra-ui/react";
import LikeCard from "./LikeCard";
import PaywallModal from "./PaywallModal";
import { GetLikesNotifications, GetMatchesNotifications } from "../services/backend";
import NoLikesMessage from "./NoLikesMessage";
import { AuthContext } from "../store/auth-context";
import { useTranslation } from "react-i18next";
import { usePremiumAccess } from "../hooks/usePremiumAccess";

function LikesAndMatchesCard({ variant = "sidebar", onUpgrade }) {
  const isPage = variant === "page";
  const isEmbedded = variant === "embedded";
  const { t } = useTranslation();
  const AuthCtx = useContext(AuthContext);
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isPremium } = usePremiumAccess();
  const user = AuthCtx.authenticatedUser;
  const [likesNotifications, setLikesNotifications] = useState();
  const [matchesNotifications, setMatchesNotifications] = useState();

  const hasValidSubscription = isPremium === true;

  const shellBg = useColorModeValue(
    "rgba(255,255,255,0.88)",
    "rgba(18, 18, 20, 0.82)"
  );
  const shellBorder = useColorModeValue("rgba(15,23,42,0.08)", "rgba(255,255,255,0.1)");
  const shellShadow = useColorModeValue(
    "0 8px 32px rgba(15,23,42,0.06), inset 0 1px 0 rgba(255,255,255,0.9)",
    "0 12px 40px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.06)"
  );
  const titleColor = useColorModeValue("gray.900", "white");
  const subtitleColor = useColorModeValue("gray.600", "whiteAlpha.600");
  const innerBg = useColorModeValue("rgba(248,250,252,0.9)", "rgba(10,10,12,0.55)");
  const innerBorder = useColorModeValue("gray.100", "whiteAlpha.100");
  /** Translucent layer so like cards show through when blurred */
  const paywallGlassBg = useColorModeValue("rgba(252, 253, 255, 0.62)", "rgba(6, 6, 8, 0.58)");
  const paywallGlassBgEmpty = useColorModeValue("rgba(248, 250, 252, 0.88)", "rgba(10, 10, 12, 0.82)");
  const paywallBorder = useColorModeValue("gray.200", "whiteAlpha.200");

  useEffect(() => {
    async function GetNotifications() {
      try {
        const likes = await GetLikesNotifications(user.id);
        const matches = await GetMatchesNotifications(user.id);
        setLikesNotifications(likes);
        setMatchesNotifications(matches);
      } catch (error) {
        toast({
          position: "bottom-left",
          title: t("likes.fetchFailed"),
          description: error.message || t("likes.fetchFailedDescription"),
          status: "error",
          duration: 15000,
          isClosable: true,
        });
      }
    }
    GetNotifications();
  }, [toast, user.id, t]);

  const handleUpgradeClick = () => {
    if (onUpgrade) {
      onUpgrade();
      return;
    }
    onOpen();
  };

  const contextValue = {
    refreshNotifications: () => {},
    handleSuccessfulMatch: () => {},
    setLikesNotifications,
    setMatchesNotifications,
    likesNotifications,
    matchesNotifications,
  };

  const likesGridScrollbarSx = {
    scrollbarWidth: "thin",
    "&::-webkit-scrollbar": { width: "6px" },
    "&::-webkit-scrollbar-thumb": {
      background: "rgba(52,211,153,0.32)",
      borderRadius: "full",
    },
  };

  const paywallUpgradeCta = (
    <>
      <Box textAlign="center" maxW="260px">
        <Text
          fontSize="10px"
          fontWeight="semibold"
          letterSpacing="0.12em"
          textTransform="uppercase"
          color={subtitleColor}
          mb={2}
        >
          {t("likes.premiumLabel")}
        </Text>
        <Text fontSize="sm" fontWeight="semibold" color={titleColor} lineHeight="short" mb={2}>
          {t("likes.upgradeTitle")}
        </Text>
        <Text fontSize="xs" color={subtitleColor} lineHeight="tall">
          {t("likes.upgradeBody")}
        </Text>
      </Box>
      <Button
        onClick={handleUpgradeClick}
        size="sm"
        w="100%"
        maxW="240px"
        px={6}
        py={5}
        fontWeight="semibold"
        borderRadius="full"
        bgGradient="linear(to-r, brand.300, brand.500)"
        color="gray.900"
        borderWidth="1px"
        borderColor="whiteAlpha.300"
        boxShadow="0 2px 12px rgba(52,211,153,0.2), inset 0 1px 0 rgba(255,255,255,0.35)"
        _hover={{
          bgGradient: "linear(to-r, brand.400, brand.600)",
          transform: "translateY(-1px)",
          boxShadow: "0 6px 20px rgba(52,211,153,0.28), inset 0 1px 0 rgba(255,255,255,0.4)",
        }}
        _active={{ transform: "translateY(0)" }}
        transition="all 0.2s"
      >
        {t("likes.upgradeToPremium")}
      </Button>
    </>
  );

  const hasLikes = Array.isArray(likesNotifications) && likesNotifications.length > 0;

  const desktopHeader = (
    <Box display={isEmbedded ? "none" : isPage ? { base: "none", lg: "block" } : { base: "none", lg: "block" }}>
      <Text
        fontSize="10px"
        fontWeight="semibold"
        letterSpacing="0.14em"
        textTransform="uppercase"
        color={subtitleColor}
        mb={1.5}
      >
        {t("settings.inbox")}
      </Text>
      <Text fontSize="lg" fontWeight="semibold" color={titleColor} letterSpacing="-0.02em" lineHeight="1.2">
        {t("likes.title")}
      </Text>
      <Text fontSize="xs" color={subtitleColor} maxW="300px" lineHeight="tall" mt={2}>
        {t("likes.subtitle")}
      </Text>
    </Box>
  );

  return (
    <>
      <Box
        w="100%"
        maxW={isPage ? { base: "100%", lg: "420px" } : isEmbedded ? "100%" : { base: "100%", lg: "360px" }}
        mb={{ base: 0, lg: isPage || isEmbedded ? 0 : 4 }}
        h={
          isEmbedded
            ? "100%"
            : isPage
            ? {
                base: "calc(100dvh - 54px - 72px - env(safe-area-inset-bottom, 0px))",
                lg: "calc(100vh - 180px)",
              }
            : {
                base: "calc(100dvh - 54px - 100px - env(safe-area-inset-bottom, 0px))",
                lg: "calc(100vh - 200px)",
              }
        }
        maxH={
          isEmbedded
            ? "100%"
            : {
                base: isPage
                  ? "calc(100dvh - 54px - 72px - env(safe-area-inset-bottom, 0px))"
                  : "calc(100dvh - 54px - 100px - env(safe-area-inset-bottom, 0px))",
                lg: "calc(100vh - 140px)",
              }
        }
        position="relative"
        overflow="hidden"
      >
        <Box
          p={{ base: 0, lg: isEmbedded ? 2 : 4 }}
          borderRadius={{ base: 0, lg: isEmbedded ? "xl" : "2xl" }}
          borderWidth={{ base: 0, lg: isEmbedded ? 0 : "1px" }}
          borderStyle="solid"
          borderColor={shellBorder}
          height="100%"
          width="100%"
          margin="0 auto"
          bg={{ base: "transparent", lg: isEmbedded ? "transparent" : shellBg }}
          backdropFilter={{ base: "none", lg: isEmbedded ? "none" : "blur(16px)" }}
          boxShadow={{ base: "none", lg: isEmbedded ? "none" : shellShadow }}
        >
          <Flex direction="column" gap={{ base: isPage ? 0 : 3, lg: isEmbedded ? 2 : 4 }} height="100%">
            {desktopHeader}

            <Box
              bg={{ base: "transparent", lg: innerBg }}
              p={{ base: isPage ? 0 : 0, lg: 3 }}
              borderRadius={{ base: 0, lg: "xl" }}
              borderWidth={{ base: 0, lg: "1px" }}
              borderStyle="solid"
              borderColor={innerBorder}
              flex={1}
              display="flex"
              flexDirection="column"
              minH={0}
              backdropFilter={{ base: "none", lg: "blur(12px)" }}
            >
              {hasValidSubscription ? (
                <>
                  {likesNotifications && likesNotifications.length === 0 && <NoLikesMessage />}
                  {hasLikes && (
                    <SimpleGrid
                      columns={2}
                      spacingX={{ base: isPage ? 2.5 : 3, lg: 3 }}
                      spacingY={{ base: isPage ? 2.5 : 2, lg: 2 }}
                      overflowY="auto"
                      py={{ base: isPage ? 1 : 0.5, lg: 0.5 }}
                      px={{ base: isPage ? 0.5 : 0, lg: 0 }}
                      flex={1}
                      minH={0}
                      alignContent="start"
                      sx={likesGridScrollbarSx}
                    >
                      {likesNotifications.map((notif) => (
                        <LikeCard
                          key={notif.likeNotificationId}
                          id={notif.user.id}
                          firstName={notif.user.firstName}
                          lastName={notif.user.lastName}
                          country={notif.user.country}
                          profileName={`${notif.user.firstName}  ${notif.user.lastName}`}
                          isLiked={notif.isliked}
                          bio={notif.user.bio}
                          age={notif.user.age}
                          profileImage={notif.user.profileImageUri}
                          images={notif.images ?? notif.user.images}
                          interests={notif.user.interests}
                          isVerified={notif.user.isVerified}
                          user={notif.user}
                          onSuccessfulMatch={contextValue.handleSuccessfulMatch}
                          likesNotifications={likesNotifications}
                          matchesNotifications={matchesNotifications}
                          setLikesNotifications={setLikesNotifications}
                          setMatchesNotifications={setMatchesNotifications}
                        />
                      ))}
                    </SimpleGrid>
                  )}
                </>
              ) : (
                <Box
                  display="grid"
                  flex={1}
                  minH={0}
                  w="100%"
                  overflow="hidden"
                  sx={{
                    gridTemplateRows: "minmax(0, 1fr)",
                    gridTemplateAreas: '"paywallStack"',
                    isolation: "isolate",
                  }}
                >
                  {hasLikes && (
                    <SimpleGrid
                      gridArea="paywallStack"
                      zIndex={0}
                      columns={2}
                      spacingX={{ base: isPage ? 2.5 : 3, lg: 3 }}
                      spacingY={{ base: isPage ? 2.5 : 3, lg: 3 }}
                      overflowY="auto"
                      px={{ base: isPage ? 0.5 : 4, lg: 2 }}
                      py={{ base: isPage ? 1 : 4, lg: 2 }}
                      minH={0}
                      alignSelf="stretch"
                      alignContent="start"
                      pointerEvents="none"
                      userSelect="none"
                      sx={likesGridScrollbarSx}
                    >
                      {likesNotifications.map((notif) => (
                        <LikeCard
                          key={notif.likeNotificationId}
                          id={notif.user.id}
                          firstName={notif.user.firstName}
                          lastName={notif.user.lastName}
                          country={notif.user.country}
                          profileName={`${notif.user.firstName}  ${notif.user.lastName}`}
                          isLiked={notif.isliked}
                          bio={notif.user.bio}
                          age={notif.user.age}
                          profileImage={notif.user.profileImageUri}
                          images={notif.images ?? notif.user.images}
                          interests={notif.user.interests}
                          isVerified={notif.user.isVerified}
                          user={notif.user}
                          onSuccessfulMatch={contextValue.handleSuccessfulMatch}
                          likesNotifications={likesNotifications}
                          matchesNotifications={matchesNotifications}
                          setLikesNotifications={setLikesNotifications}
                          setMatchesNotifications={setMatchesNotifications}
                        />
                      ))}
                    </SimpleGrid>
                  )}
                  <Flex
                    gridArea="paywallStack"
                    zIndex={2}
                    alignSelf="stretch"
                    direction="column"
                    align="center"
                    justify="center"
                    gap={4}
                    borderRadius={{ base: 0, lg: "xl" }}
                    borderWidth={{ base: 0, lg: "1px" }}
                    borderStyle="solid"
                    borderColor={paywallBorder}
                    bg={hasLikes ? paywallGlassBg : paywallGlassBgEmpty}
                    backdropFilter={hasLikes ? "blur(14px) saturate(140%)" : "blur(12px) saturate(130%)"}
                    sx={{
                      WebkitBackdropFilter: hasLikes
                        ? "blur(14px) saturate(140%)"
                        : "blur(12px) saturate(130%)",
                    }}
                    p={5}
                    pointerEvents="auto"
                    boxShadow="inset 0 1px 0 rgba(255,255,255,0.06)"
                  >
                    {paywallUpgradeCta}
                  </Flex>
                </Box>
              )}
            </Box>
          </Flex>
        </Box>
      </Box>

      <PaywallModal isOpen={isOpen} onClose={onClose} userId={user?.id} />
    </>
  );
}

export default LikesAndMatchesCard;
